import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '~/libs/api';
import type {
  TDocumentGenerationBank,
  TDocumentGenerationBanksResponse,
  TDocumentGenerationCurrency,
  TThunkError,
} from '~/store/covenants/types.ts';
import { thunkErrorHandler } from '~/store/utils.ts';

const BANKS_PAGE_SIZE = 100;

const fetchDocumentGenerationBanksPage = async (page: number) => {
  const { data } = await api.get<TDocumentGenerationBanksResponse>(
    '/dictionary-service/bank/get-info-by-filter',
    {
      params: {
        page,
        size: BANKS_PAGE_SIZE,
      },
    }
  );

  return data;
};

export const fetchDocumentGenerationBanks = createAsyncThunk<
  TDocumentGenerationBank[],
  void,
  { rejectValue: TThunkError }
>('covenantsPage/fetchDocumentGenerationBanks', async (_, { rejectWithValue }) => {
  try {
    const firstPage = await fetchDocumentGenerationBanksPage(0);
    const otherPages = await Promise.all(
      Array.from({ length: Math.max(firstPage.totalPages - 1, 0) }, (_, index) =>
        fetchDocumentGenerationBanksPage(index + 1)
      )
    );
    const banks = [firstPage, ...otherPages].flatMap(({ content }) => content);

    return Array.from(new Map(banks.map((bank) => [bank.id, bank])).values());
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const fetchDocumentGenerationCurrencies = createAsyncThunk<
  TDocumentGenerationCurrency[],
  void,
  { rejectValue: TThunkError }
>('covenantsPage/fetchDocumentGenerationCurrencies', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get<TDocumentGenerationCurrency[]>('/dictionary-service/currency');

    return Array.isArray(data) ? data : [];
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});
